<template>
  <v-row class="fill-height scroll-container" ref="scrollContainer" v-on:scroll="handleScroll">
    <v-col md="8" class="pr-4 content-height overflow-y-auto">

      <v-row v-if="loading" class="text-center fill-height" align="center">
        <v-col>
          <v-progress-circular color="primary" indeterminate size="48" />
        </v-col>
      </v-row>

      <template v-if="!loading">
      <template v-if="getConnectedDevices && getConnectedDevices.length > 0">
        <span class="Associated-Devices">{{ $t('assets.locations.locationDevices.connectedDevices') }}</span>
        <v-row class="px-2 mt-3 mb-2">
          <v-col
            cols="12" sm="12" md="6" lg="6" xl="6"
            class="px-2 pb-4 pt-0"
            v-for="item in getConnectedDevices"
            :key="item.uuid">
            <LocationDevices :data="item" :type="item.device_type_shorthand" />
          </v-col>
        </v-row>
      </template>

      <!-- Disconnected Devices -->
      <template v-if="getDisconnectedDevices && getDisconnectedDevices.length > 0">
        <span class="Associated-Devices">{{ $t('assets.locations.locationDevices.disconnectedDevices') }}</span>
        <v-row class="px-2 mt-3 mb-2">
          <v-col
            cols="12" sm="12" md="6" lg="6" xl="6"
            class="px-2 pb-4 pt-0"
            v-for="item in getDisconnectedDevices"
            :key="item.uuid">
            <LocationDevices :data="item" :type="item.device_type_shorthand" />
          </v-col>
        </v-row>
      </template>

      <!-- Non-Connected Devices -->
      <template v-if="getNonConnectedDevices && getNonConnectedDevices.length > 0">
        <span class="Associated-Devices">{{ $t('assets.locations.locationDevices.nonConnectedDevices') }}</span>
        <v-row class="px-2 mt-3 mb-2">
          <v-col
            cols="12" sm="12" md="6" lg="6" xl="6"
            class="px-2 pb-4 pt-0"
            v-for="item in getNonConnectedDevices"
            :key="item.uuid">
            <LocationDevices :data="item" :type="item.device_type_shorthand" />
          </v-col>
        </v-row>
      </template>

      <!-- Archived Devices -->
      <template v-if="getArchivedDevices && getArchivedDevices.length > 0">
        <span class="Associated-Devices">{{ $t('assets.locations.locationDevices.archivedDevices') }}</span>
        <v-row class="px-2 mt-3 mb-2">
          <v-col
            cols="12" sm="12" md="6" lg="6" xl="6"
            class="px-2 pb-4 pt-0"
            v-for="item in getArchivedDevices"
            :key="item.uuid">
            <LocationDevices :data="item" :type="item.device_type_shorthand" />
          </v-col>
        </v-row>
      </template>

      <!-- Installation Pending Devices -->
      <template v-if="getInstallationPendingDevices && getInstallationPendingDevices.length > 0">
        <span class="Associated-Devices">{{ $t('assets.locations.locationDevices.installationPending') }}</span>
        <v-row class="px-2 mt-3 mb-2">
          <v-col
            cols="12" sm="12" md="6" lg="6" xl="6"
            class="px-2 pb-4 pt-0"
            v-for="item in getInstallationPendingDevices"
            :key="item.uuid">
            <LocationDevices :data="item" :type="item.device_type_shorthand" />
          </v-col>
        </v-row>
      </template>

      <!-- Empty state: no devices in any category -->
      <div v-if="hasNoDevices" class="report-overview mt-5 px-4 py-3">
        <span>{{ $t('assets.locations.locationDevices.noAssociated') }}</span>
      </div>
      </template>
    </v-col>
    <v-divider class="d-none d-md-flex" vertical />
    <v-col md="4" class="ml-n3 py-0 pr-0 content-height overflow-y-auto">
      <v-card height="72" class="px-4 pb-5 pt-3" flat>
        <v-card-text class="pa-0">
          <span class="subtitle-2 mb-2 text-secondary font-weight-regular d-block">
            {{ $t('assets.locations.locationDevices.associated') }}
          </span>
          <v-list-item-title class="subtitle-2 text-primary font-weight-regular">
            {{ countTotal }}
          </v-list-item-title>
        </v-card-text>
      </v-card>
      <v-divider />
      <v-card height="auto" class="px-4 pb-5 pt-3" flat>
        <v-card-text class="pa-0">
          <span class="subtitle-2 mb-2 text-secondary font-weight-regular d-block">
            {{ $t('assets.locations.locationDevices.structure') }}
          </span>
          <v-list-item-title class="subtitle-2 text-primary font-weight-regular">
            {{ countConnected }} {{ $t('assets.locations.locationDevices.connected') }}, {{ countDisconnected }} {{ $t('assets.locations.locationDevices.disconnected') }},
            <br />
            {{ countNonConnected }} {{ $t('assets.locations.locationDevices.nonConnected') }}, {{ countArchived }} {{ $t('assets.locations.locationDevices.archived') }},
            <br/>
            {{ countInstallationPending }} {{ $t('assets.locations.locationDevices.installationPending') }}.
          </v-list-item-title>
        </v-card-text>
      </v-card>
      <v-divider />
      <v-card height="72" class="px-4 pb-5 pt-3" flat>
        <v-card-text class="pa-0">
          <span class="subtitle-2 mb-2 text-secondary font-weight-regular d-block">
            {{ $t('assets.locations.locationDevices.latestAssociated') }}
          </span>
          <v-list-item-title class="subtitle-2 text-primary font-weight-regular">
            {{ getLatestDevice || 'N/A' }}
          </v-list-item-title>
        </v-card-text>
      </v-card>
      <v-divider />
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import LocationDevices from "../LocationDevices";

export default {
  name: "locationDevicesList",
  components: {
    LocationDevices,
  },
  props: ["location"],
  mounted: function() {
    if (this.$props.location && this.$props.location.uuid) {
      this.fetchAllDevices();
    }
  },
  data() {
    return {
      scrollTimeout: null,
      loading: false,
    };
  },
  computed: {
    ...mapGetters("locations", [
      "getLinksLocations",
      "getConnectedDevices",
      "getDisconnectedDevices",
      "getNonConnectedDevices",
      "getArchivedDevices",
      "getInstallationPendingDevices",
    ]),
    hasNoDevices() {
      return (
        (!this.getConnectedDevices || this.getConnectedDevices.length === 0) &&
        (!this.getDisconnectedDevices || this.getDisconnectedDevices.length === 0) &&
        (!this.getNonConnectedDevices || this.getNonConnectedDevices.length === 0) &&
        (!this.getArchivedDevices || this.getArchivedDevices.length === 0) &&
        (!this.getInstallationPendingDevices || this.getInstallationPendingDevices.length === 0)
      );
    },
    getLatestDevice() {
      const all = [
        ...(this.getConnectedDevices || []),
        ...(this.getDisconnectedDevices || []),
        ...(this.getNonConnectedDevices || []),
        ...(this.getArchivedDevices || []),
        ...(this.getInstallationPendingDevices || []),
      ];
      return all.length > 0 && all[0].name ? all[0].name : '';
    },
    countConnected() {
      return this.getConnectedDevices ? this.getConnectedDevices.length : 0;
    },
    countDisconnected() {
      return this.getDisconnectedDevices ? this.getDisconnectedDevices.length : 0;
    },
    countNonConnected() {
      return this.getNonConnectedDevices ? this.getNonConnectedDevices.length : 0;
    },
    countArchived(){
      return this.getArchivedDevices ? this.getArchivedDevices.length : 0;
    },
    //count installation pending devices  
    countInstallationPending() {
      return this.getInstallationPendingDevices ? this.getInstallationPendingDevices.length : 0;
     },
    countArchivedCameras(){
      return this.getArchivedDevices ? this.getArchivedDevices.length : 0;
    },
    
    countTotal() {
      return this.countConnected + this.countDisconnected + this.countNonConnected + this.countArchived + this.countInstallationPending;
    },
  },
  watch: {
    'location.uuid'(uuid) {
      if (uuid) this.fetchAllDevices();
    }
  },
  beforeDestroy() {
    this.$store.commit('locations/SET_CONNECTED_DEVICES', []);
    this.$store.commit('locations/SET_DISCONNECTED_DEVICES', []);
    this.$store.commit('locations/SET_NON_CONNECTED_DEVICES', []);
    this.$store.commit('locations/SET_ARCHIVED_DEVICES', []);
    this.$store.commit('locations/SET_INSTALLATION_PENDING_DEVICES', []);
  },
  methods: {
    ...mapActions({
      getConnectedLocationDevices: "locations/getConnectedLocationDevices",
      getDisconnectedLocationDevices: "locations/getDisconnectedLocationDevices",
      getNonConnectedLocationDevices: "locations/getNonConnectedLocationDevices",
      getArchivedLocationDevices: "locations/getArchivedLocationDevices",
      getInstallationPendingLocationDevices: "locations/getInstallationPendingLocationDevices",
      getSpecificDevice: "devices/getSpecificDevice",
    }),
    fetchAllDevices() {
      const params = {
        space_uuid: this.location.space_uuid,
        location_uuid: this.location.uuid,
      };
      this.loading = true;
      this.$store.commit('global/SET_LOADING', true);
      Promise.all([
        this.getConnectedLocationDevices(params),
        this.getDisconnectedLocationDevices(params),
        this.getNonConnectedLocationDevices(params),
        this.getArchivedLocationDevices(params),
        this.getInstallationPendingLocationDevices(params),
      ]).finally(() => {
        this.loading = false;
        this.$store.commit('global/SET_LOADING', false);
      });
    },
    debouncedHandleScroll() {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(this.handleScroll, 200);
    },
    handleScroll() {
      const container = this.$refs.scrollContainer;
      const bottomOfContainer = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (bottomOfContainer && this.getLinksLocations.next) {
        let nextNumber = this.extractPageFromLinkNext();
        this.$store.commit("locations/SET_PAGE_NUMBER", nextNumber);
      }
    },
    extractPageFromLinkNext() {
      const url = this.getLinksLocations.next;
      const parsedUrl = new URL(url);
      const page = parsedUrl.searchParams.get("page");
      return page;
    },
  },
};
</script>

<style scoped>
.report-overview {
  background: var(--v-noticeOn-base);
  border: 1px solid #2B85D8;
  color: var(--v-noticeMntOffText-base);
  box-sizing: border-box;
  border-radius: 4px;
  font-size: small;
}
.scroll-container {
  height: 100%; /* Set the desired height */
  overflow-y: auto; /* Enable vertical scrollbar */
}
.Associated-Devices {
  width: 608px;
  height: 19px;
  margin: 0 0 24px;
  font-family: Roboto;
  font-size: 16px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: var(--v-secondaryText-base);
}
</style>
